import React from 'react';
import {
  Dialog,
  DialogContent,
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Stack,
  Divider,
  Paper,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { Producto } from '../../types';

interface InactivarProductoModalProps {
  open: boolean;
  producto: Producto | null;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function InactivarProductoModal({
  open,
  producto,
  loading = false,
  onConfirm,
  onCancel,
}: InactivarProductoModalProps): React.ReactElement {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      aria-labelledby="inactivar-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: 2,
          backgroundColor: '#f5f5f5',
          backgroundImage: 'none',
          p: 1,
        },
      }}
    >
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
          backgroundColor: '#ffffff',
          border: '1px solid rgba(160, 88, 142, 0.08)',
          overflow: 'hidden',
        }}
      >
        {/* Header compacto con fondo degradado */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #a0588e 0%, #7a3d6e 100%)',
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <WarningIcon sx={{ color: '#ffffff', fontSize: 16 }} />
          </Box>
          <Typography
            id="inactivar-dialog-title"
            variant="body2"
            sx={{
              color: '#ffffff',
              fontWeight: 600,
              m: 0,
              fontSize: '0.9rem',
            }}
          >
            Desactivar Producto
          </Typography>
        </Box>

        <CardContent sx={{ p: 1.5 }}>
            {producto && (
              <Stack spacing={1.5}>
                {/* Aviso descriptivo */}
                <Box
                  sx={{
                    backgroundColor: '#f7edf5',
                    border: '1px solid #e8d0e2',
                    borderRadius: 2,
                    p: 1.25,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#7a3d6e',
                      lineHeight: 1.5,
                      fontWeight: 500,
                    }}
                  >
                    Se desactivará el producto{' '}
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 700,
                        color: '#a0588e',
                        fontSize: 'inherit',
                      }}
                    >
                      "{producto.nombre}"
                    </Typography>
                    . No aparecerá en el listado de productos disponibles.
                  </Typography>
                </Box>

                {/* Información del producto */}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#999',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      display: 'block',
                      mb: 1,
                      fontSize: '0.7rem',
                    }}
                  >
                    Detalles del Producto
                  </Typography>

                  <Stack spacing={1}>
                    {/* Código */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '6px 0',
                      }}
                    >
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        Código:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#7a3d6e',
                          fontWeight: 600,
                          fontFamily: 'monospace',
                        }}
                      >
                        {producto.codigo}
                      </Typography>
                    </Box>

                    {/* Precio */}
                    {producto.precioVenta > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '10px 0',
                        }}
                      >
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          Precio de Venta:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#a0588e',
                            fontWeight: 700,
                            fontSize: '1rem',
                          }}
                        >
                          ${producto.precioVenta.toLocaleString('es-CO')}
                        </Typography>
                      </Box>
                    )}

                    {/* Stock */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '6px 0',
                      }}
                    >
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        Stock Disponible:
                      </Typography>
                      <Paper
                        sx={{
                          px: 2,
                          py: 0.5,
                          backgroundColor:
                            producto.cantidadStock > 10
                              ? '#eaf5f2'
                              : '#fdf0f0',
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              producto.cantidadStock > 10
                                ? '#3d7a6e'
                                : '#8a3d3d',
                            fontWeight: 600,
                          }}
                        >
                          {producto.cantidadStock} unidades
                        </Typography>
                      </Paper>
                    </Box>
                  </Stack>
                </Box>

                <Divider sx={{ borderColor: '#e8d0e2' }} />

                {/* Acciones */}
                <Stack direction="row" spacing={1.5} sx={{ pt: 0.5 }}>
                  <Button
                    onClick={onCancel}
                    disabled={loading}
                    variant="outlined"
                    fullWidth
                    sx={{
                      borderColor: '#d0d0d0',
                      color: '#666',
                      fontWeight: 600,
                      borderRadius: 2,
                      py: 0.75,
                      '&:hover': {
                        borderColor: '#a0588e',
                        backgroundColor: '#f7edf5',
                      },
                      '&:disabled': {
                        borderColor: '#ddd',
                        color: '#ccc',
                      },
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={onConfirm}
                    disabled={loading}
                    variant="contained"
                    fullWidth
                    sx={{
                      background: 'linear-gradient(135deg, #a0588e 0%, #7a3d6e 100%)',
                      color: '#ffffff',
                      fontWeight: 600,
                      borderRadius: 2,
                      py: 1.2,
                      boxShadow: '0 4px 12px rgba(160, 88, 142, 0.3)',
                      '&:hover': {
                        boxShadow: '0 8px 24px rgba(160, 88, 142, 0.4)',
                        transform: 'translateY(-2px)',
                        transition: 'all 0.2s ease-in-out',
                      },
                      '&:disabled': {
                        background: '#ccc',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {loading ? 'Desactivando...' : 'Desactivar'}
                  </Button>
                </Stack>
              </Stack>
            )}
          </CardContent>
        </Card>
    </Dialog>
  );
}
